import { appStrings } from "utils/appStrings";
import QuizItem from "./QuizItem";
import { useTrail, useSpring, useChain, animated } from "react-spring";
import { useRef } from "react";
import { useSetRecoilState } from "recoil";
import { quizState } from "atoms/quiz";
import { IQuiz } from "models/quiz";
import { NavLink, useHistory } from "react-router-dom";
import {
  currentProblemState,
  currentStepState,
  stepTypeState,
  totalStepState,
} from "atoms/controller";
import { problemState } from "atoms/problems";
import { StepTypes } from "models/controller";
import { getTotalSteps } from "utils/functions";
import { useMutation, useQuery } from "@apollo/client";
import { GET_ALL_QUIZZES, START_QUIZZ } from "graphql/quiz";
import PageLoader from "components/Layout/PageLoader";
import AuthPane from "components/Home/AuthPane";
import { toast } from "react-toastify";
import { answerSheetState } from "atoms/answerSheet";
import { userQuizState } from "atoms/userQuiz";
import { toNumber } from "lodash";

const QuizList: React.FC = () => {
  const setQuiz = useSetRecoilState(quizState);
  const setTotalSteps = useSetRecoilState(totalStepState);
  const setProblems = useSetRecoilState(problemState);
  const setStep = useSetRecoilState(currentStepState);
  const setProblem = useSetRecoilState(currentProblemState);
  const setStepType = useSetRecoilState(stepTypeState);
  const setUserQuiz = useSetRecoilState(userQuizState);
  const setAnswerSheet = useSetRecoilState(answerSheetState);
  const trailRef = useRef(null);
  const hintRef = useRef(null);
  const { push } = useHistory();
  const { loading, error, data, refetch } = useQuery(GET_ALL_QUIZZES, {
    notifyOnNetworkStatusChange: true,
  });
  const quizzes: IQuiz[] = data ? data.QUIZ_schemas : [];

  //Graphql queries

  const [start, { loading: startLoading }] = useMutation(START_QUIZZ, {
    onError: (error) => toast.error(error.message),
    onCompleted: (data) => {
      const userQuiz = data.start_quiz;
      const quiz = userQuiz.schema;
      setStep(0);
      setProblem(-1);
      setStepType(StepTypes.INTRO);
      setQuiz(quiz);
      setTotalSteps(getTotalSteps(quiz.problems.length));
      setProblems(quiz.problems);
      setAnswerSheet({});
      setUserQuiz({
        id: userQuiz.id,
        score: userQuiz.score,
        token: userQuiz.access_token,
        result: userQuiz.result,
      });
      push(`/quiz/${data.start_quiz.id}?qt=${userQuiz.access_token}`);
    },
  });

  const onQuizClick = (selectedQuiz: IQuiz) => {
    start({
      variables: {
        schema_id: toNumber(selectedQuiz.id),
      },
    });
  };

  const transitions = useTrail(quizzes.length, {
    ref: trailRef,
    config: { mass: 5, tension: 3000, friction: 300 },
    transform: "scale(1)",
    from: { transform: "scale(0)" },
  });

  const hintAnimation = useSpring({
    ref: hintRef,
    transform: "scale(1)",
    from: { transform: "scale(0)" },
  });

  useChain([hintRef, trailRef], [0, 0.3]);

  return (
    <div className="w-full">
      <animated.div
        style={hintAnimation}
        className="py-6 px-16 flex flex-wrap justify-between items-center mb-8 dark:bg-gray-800"
      >
        <NavLink
          to="/"
          className="flex flex-row items-center font-semibold text-white text-lg dark:text-gray-100"
        >
          <img
            src="/assets/img/logo-white.svg"
            className="w-6 ml-4"
            alt={appStrings.brand}
          />
          {appStrings.brand}
        </NavLink>
        <div className="text-white">
          <AuthPane />
        </div>
      </animated.div>
      <PageLoader
        loading={loading || startLoading}
        error={error}
        onRefresh={refetch}
      >
        <div className="container mx-auto px-8 flex flex-col space-y-6 md:grid md:grid-cols-2 md:space-y-0 md:gap-6 lg:grid-cols-4">
          {transitions.map((props, key) => (
            <animated.div key={key} style={props}>
              <QuizItem
                title={quizzes[key].title}
                image={quizzes[key].image_url}
                onClick={() => onQuizClick(quizzes[key])}
              />
            </animated.div>
          ))}
        </div>
      </PageLoader>
    </div>
  );
};

export default QuizList;
